import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  bulkCspmCheckSuppressions,
  getCspmSuppressionChecks,
} from "../../../helpers/auth_aws_helper";
import {
  CloudProviderContext,
  shouldShowAzurePlaceholderView,
} from "../../../CloudProviderContext";
import { formatScheduleName, getSuppressionAccountLabels } from "../Suppression/utils/scheduleNameFormatter";
import { TruncatedTooltipText } from "../../../components/AccountDetailPolicy/editAccountTooltipStyles";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";
import { EditAccountSortableHeader } from "../../../components/AccountDetailPolicy/editAccountTableControls";
import {
  BODY_CELL_SX,
  CELL_TEXT,
  CHECKBOX_SX,
  ConfigCard,
  EmptyRow,
  ErrorBanner,
  HEADER_CELL_SX,
  ListFooter,
  LoadingState,
  MENU_PROPS,
  TableFrame,
} from "./ui";
import {
  DEFAULT_REMOVE_REASON,
  DEFAULT_SUPPRESS_REASON,
  SEVERITY_OPTIONS,
  countCheckChanges,
  cspmProviderParam,
  formatLabel,
  isCheckChange,
  normalizedReason,
  readFlag,
  readMutationMessage,
  readPagedList,
  readStoredPageSize,
  readStoredSchedule,
  resolveCheckState,
  severityStyle,
  storePageSize,
} from "./utils";

const PAGE_SIZE_KEY = "cspm_configuration_check_page_size";
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const MAX_CHECK_PAGES = 20;

function severityKey(value) {
  return String(value || "").trim().toUpperCase();
}

function matchesSeverity(check, severity) {
  if (!severity) return true;
  return severityKey(check?.severity) === severity;
}

function slicePage(rows, page, pageSize) {
  const start = Math.max(0, (page - 1) * pageSize);
  return {
    items: rows.slice(start, start + pageSize),
    page,
    pageSize,
    total: rows.length,
  };
}

const SEVERITY_RANK = {
  INFORMATIONAL: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
  CRITICAL: 5,
};

function sortText(value) {
  return String(value || "").trim().toLocaleLowerCase();
}

function statusSortValue(check) {
  const active = readFlag(check?.is_active);
  if (active === null) return "";
  return active ? "active" : "inactive";
}

function compareChecks(left, right, field) {
  switch (field) {
    case "severity":
      return (SEVERITY_RANK[severityKey(left?.severity)] || 99) - (SEVERITY_RANK[severityKey(right?.severity)] || 99);
    case "service":
      return sortText(left?.service).localeCompare(sortText(right?.service));
    case "resourceType":
      return sortText(left?.resource_type).localeCompare(sortText(right?.resource_type));
    case "reason":
      return sortText(left?.reason).localeCompare(sortText(right?.reason));
    case "status":
      return statusSortValue(left).localeCompare(statusSortValue(right));
    case "check":
    default: {
      const byTitle = sortText(left?.title || left?.check_id).localeCompare(sortText(right?.title || right?.check_id));
      if (byTitle !== 0) return byTitle;
      return sortText(left?.check_id).localeCompare(sortText(right?.check_id));
    }
  }
}

function sortChecks(rows, field, direction) {
  const copy = rows.map((row, index) => ({ row, index }));
  copy.sort((left, right) => {
    const compared = compareChecks(left.row, right.row, field);
    if (compared !== 0) return direction === "asc" ? compared : -compared;
    return left.index - right.index;
  });
  return copy.map((entry) => entry.row);
}

async function collectFilteredChecks(requestPage, severityQuery, isStale) {
  const apiPageSize = 100;
  const loadPages = async (severityValue) => {
    const firstPage = readPagedList(
      await requestPage(1, apiPageSize, severityValue),
      "Unable to load CSPM checks."
    );
    if (isStale()) return null;
    const pageCount = Math.min(
      MAX_CHECK_PAGES,
      Math.max(1, Math.ceil((firstPage.total || firstPage.items.length) / apiPageSize))
    );
    const rest = pageCount > 1
      ? await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) => (
          requestPage(index + 2, apiPageSize, severityValue).then((response) => (
            readPagedList(response, "Unable to load CSPM checks.")
          ))
        ))
      )
      : [];
    if (isStale()) return null;
    return [firstPage, ...rest].flatMap((pageList) => pageList.items);
  };

  let items = await loadPages(severityQuery);
  if (!items) return null;
  if (severityQuery && items.length === 0) {
    const lowerItems = await loadPages(severityQuery.toLowerCase());
    if (!lowerItems) return null;
    if (lowerItems.length > 0) items = lowerItems;
  }
  if (severityQuery && items.some((check) => !matchesSeverity(check, severityQuery))) {
    const unfiltered = await loadPages("");
    if (!unfiltered) return null;
    items = unfiltered;
  }
  if (severityQuery) return items.filter((check) => matchesSeverity(check, severityQuery));
  return items;
}

const BACK_BUTTON_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  color: "#334155",
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  height: 40,
  px: 2,
  flexShrink: 0,
  alignSelf: { xs: "stretch", sm: "flex-start" },
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
    color: "#111827",
  },
};

const SAVE_BUTTON_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  textTransform: "none",
  backgroundColor: "#F98125",
  color: "#fff",
  fontWeight: 600,
  borderRadius: "8px",
  height: 40,
  minWidth: 88,
  px: 2,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#e8741c", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#fdba74", color: "#fff" },
};

const TOOLBAR_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#111827", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
    "&::placeholder": { color: "#94a3b8", opacity: 1 },
  },
};

const TOOLBAR_SELECT_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  height: 40,
  width: "100%",
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#111827", borderWidth: "1px" },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: 40,
    minWidth: 0,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": { color: "#94a3b8" },
};

const RESET_BUTTON_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  color: "#334155",
  borderColor: "#e2e8f0",
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  height: 40,
  px: 2,
  borderRadius: "8px",
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
    color: "#111827",
  },
  "&.Mui-disabled": {
    backgroundColor: "#fff",
    borderColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const REASON_FIELD_SX = {
  width: "100%",
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    height: 36,
    fontSize: "0.8125rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#111827", borderWidth: "1px" },
    "&.Mui-disabled": { backgroundColor: "#f8fafc" },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const CHIP_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  height: 22,
  fontSize: "0.6875rem",
  fontWeight: 700,
  borderRadius: "6px",
  "& .MuiChip-label": { px: 1 },
};

const CheckSuppressions = ({ scheduleId }) => {
  const history = useHistory();
  const location = useLocation();
  const { selectedProvider } = useContext(CloudProviderContext);
  const isAzureView = shouldShowAzurePlaceholderView(selectedProvider);
  const accountLabels = getSuppressionAccountLabels(isAzureView);
  const provider = cspmProviderParam(selectedProvider);

  const schedule = useMemo(() => {
    const fromState = location.state?.schedule;
    if (fromState?.schedule_id === scheduleId) return fromState;
    return readStoredSchedule(scheduleId);
  }, [location.state, scheduleId]);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [severity, setSeverity] = useState("");
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readStoredPageSize(PAGE_SIZE_KEY, 25, PAGE_SIZE_OPTIONS));
  const [checksResult, setChecksResult] = useState(null);
  const [pending, setPending] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const requestRef = useRef(0);
  const severityCacheRef = useRef({ key: "", items: null });
  const sortListRef = useRef({ key: "", items: null });
  const hasResultRef = useRef(false);

  const changeCount = useMemo(() => countCheckChanges(pending), [pending]);
  const items = useMemo(() => {
    const rows = checksResult?.items || [];
    const selected = severityKey(severity);
    if (!selected) return rows;
    return rows.filter((check) => matchesSeverity(check, selected));
  }, [checksResult, severity]);
  const appliedSearch = useRef("");

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (appliedSearch.current === next) return;
      appliedSearch.current = next;
      setSearch(next);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!scheduleId) return undefined;
    let cancelled = false;
    setServicesLoading(true);

    const loadServices = async () => {
      const apiPageSize = 100;
      const requestPage = (pageNumber) => getCspmSuppressionChecks({
        schedule_id: scheduleId,
        page: pageNumber,
        page_size: apiPageSize,
        ...(provider ? { provider } : {}),
      });
      const firstPage = readPagedList(await requestPage(1), "Unable to load services.");
      if (cancelled) return;
      const pageCount = Math.min(
        MAX_CHECK_PAGES,
        Math.max(1, Math.ceil((firstPage.total || firstPage.items.length) / apiPageSize))
      );
      const rest = pageCount > 1
        ? await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) => (
            requestPage(index + 2).then((response) => readPagedList(response, "Unable to load services."))
          ))
        )
        : [];
      if (cancelled) return;
      const names = new Set();
      [firstPage, ...rest].forEach((pageList) => {
        pageList.items.forEach((check) => {
          const value = String(check.service || "").trim();
          if (value) names.add(value);
        });
      });
      const options = [...names].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
      setServiceOptions(options);
      setService((current) => {
        if (current && !options.includes(current)) {
          setPage(1);
          return "";
        }
        return current;
      });
    };

    loadServices()
      .catch(() => {
        if (!cancelled) setServiceOptions([]);
      })
      .finally(() => {
        if (!cancelled) setServicesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleId, provider]);

  const loadChecks = useCallback(async () => {
    if (!scheduleId) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setError(null);
    if (hasResultRef.current) setRefreshing(true);
    else setLoading(true);

    const severityQuery = severityKey(severity);
    const cacheKey = [scheduleId, provider, severityQuery, service, search].join("\u0000");
    const requestPage = (pageNumber, size, severityValue) => getCspmSuppressionChecks({
      schedule_id: scheduleId,
      page: pageNumber,
      page_size: size,
      ...(provider ? { provider } : {}),
      ...(severityValue ? { severity: severityValue } : {}),
      ...(service ? { service } : {}),
      ...(search ? { search } : {}),
    });

    try {
      if (orderBy) {
        let rows = sortListRef.current.key === cacheKey ? sortListRef.current.items : null;
        if (!rows) {
          rows = await collectFilteredChecks(
            requestPage,
            severityQuery,
            () => requestId !== requestRef.current
          );
          if (requestId !== requestRef.current || !rows) return;
          sortListRef.current = { key: cacheKey, items: rows };
        }
        if (requestId !== requestRef.current) return;
        hasResultRef.current = true;
        setChecksResult(slicePage(sortChecks(rows, orderBy, order), page, pageSize));
        return;
      }
      sortListRef.current = { key: "", items: null };

      const cached = severityCacheRef.current;
      if (severityQuery && cached.key === cacheKey && Array.isArray(cached.items)) {
        if (requestId !== requestRef.current) return;
        hasResultRef.current = true;
        setChecksResult(slicePage(cached.items, page, pageSize));
        return;
      }

      const requestedSize = Math.min(pageSize, 100);
      let list = readPagedList(
        await requestPage(page, requestedSize, severityQuery),
        "Unable to load CSPM checks."
      );
      if (requestId !== requestRef.current) return;

      if (severityQuery && list.total === 0) {
        const lowerList = readPagedList(
          await requestPage(page, requestedSize, severityQuery.toLowerCase()),
          "Unable to load CSPM checks."
        );
        if (requestId !== requestRef.current) return;
        if (lowerList.total > 0) list = lowerList;
      }

      const serverApplied = !severityQuery || list.items.every((check) => matchesSeverity(check, severityQuery));
      if (severityQuery && !serverApplied) {
        const apiPageSize = 100;
        const firstPage = readPagedList(
          await requestPage(1, apiPageSize, ""),
          "Unable to load CSPM checks."
        );
        if (requestId !== requestRef.current) return;
        const pageCount = Math.min(
          MAX_CHECK_PAGES,
          Math.max(1, Math.ceil((firstPage.total || firstPage.items.length) / apiPageSize))
        );
        const rest = pageCount > 1
          ? await Promise.all(
            Array.from({ length: pageCount - 1 }, (_, index) => (
              requestPage(index + 2, apiPageSize, "").then((response) => (
                readPagedList(response, "Unable to load CSPM checks.")
              ))
            ))
          )
          : [];
        if (requestId !== requestRef.current) return;
        const collected = [firstPage, ...rest].flatMap((pageList) => pageList.items);
        const filtered = collected.filter((check) => matchesSeverity(check, severityQuery));
        severityCacheRef.current = { key: cacheKey, items: filtered };
        list = slicePage(filtered, page, pageSize);
      } else if (severityQuery) {
        severityCacheRef.current = { key: "", items: null };
        list = {
          ...list,
          items: list.items.filter((check) => matchesSeverity(check, severityQuery)),
        };
      } else {
        severityCacheRef.current = { key: "", items: null };
      }

      hasResultRef.current = true;
      setChecksResult(list);
    } catch (loadError) {
      if (requestId !== requestRef.current) return;
      setError(loadError.message || "Unable to load CSPM checks.");
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [scheduleId, page, pageSize, provider, severity, service, search, orderBy, order]);

  useEffect(() => {
    loadChecks();
  }, [loadChecks]);

  useEffect(() => {
    if (changeCount === 0) return undefined;
    const unblock = history.block((nextLocation) => {
      if (nextLocation?.pathname === location.pathname) return undefined;
      return "You have unsaved CSPM check changes. Leave without saving?";
    });
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [changeCount, history, location.pathname]);

  const commitCheck = (check, patch) => {
    if (!check?.check_id || saving) return;
    setPending((current) => {
      const next = new Map(current);
      const updated = { ...resolveCheckState(check, next), ...patch };
      if (isCheckChange(updated)) next.set(check.check_id, updated);
      else next.delete(check.check_id);
      return next;
    });
  };

  const selectableItems = items.filter((check) => check.check_id);
  const suppressedOnPage = selectableItems.filter(
    (check) => resolveCheckState(check, pending).suppressed
  ).length;
  const allChecked = selectableItems.length > 0 && suppressedOnPage === selectableItems.length;
  const someChecked = suppressedOnPage > 0 && !allChecked;

  const togglePage = (checked) => {
    if (saving) return;
    setPending((current) => {
      const next = new Map(current);
      selectableItems.forEach((check) => {
        const base = resolveCheckState(check, next);
        const updated = {
          ...base,
          suppressed: checked,
          reason: checked ? base.reason || base.originalReason || "" : base.reason,
        };
        if (isCheckChange(updated)) next.set(check.check_id, updated);
        else next.delete(check.check_id);
      });
      return next;
    });
  };

  const saveChanges = async () => {
    const creates = [];
    const deletes = [];
    pending.forEach((entry, checkId) => {
      if (!checkId || !isCheckChange(entry)) return;
      if (entry.suppressed) {
        creates.push({ check_id: checkId, reason: normalizedReason(entry) });
      } else if (entry.wasSuppressed) {
        deletes.push({ check_id: checkId, reason: DEFAULT_REMOVE_REASON });
      }
    });
    if (creates.length === 0 && deletes.length === 0) return;

    setSaving("save");
    let mutated = false;
    try {
      if (deletes.length > 0) {
        const message = readMutationMessage(
          await bulkCspmCheckSuppressions(scheduleId, deletes, { delete: true })
        );
        mutated = true;
        if (creates.length === 0) enqueueSnackbar(message, { variant: "success" });
      }
      if (creates.length > 0) {
        const message = readMutationMessage(await bulkCspmCheckSuppressions(scheduleId, creates));
        mutated = true;
        enqueueSnackbar(message, { variant: "success" });
      }
      setPending(new Map());
    } catch (saveError) {
      enqueueSnackbar(saveError.message || "Unable to save check suppressions.", { variant: "error" });
      if (mutated) setPending(new Map());
    } finally {
      setSaving(null);
      if (mutated) {
        severityCacheRef.current = { key: "", items: null };
        sortListRef.current = { key: "", items: null };
        loadChecks();
      }
    }
  };

  const resetSuppressions = async () => {
    setSaving("reset");
    try {
      const message = readMutationMessage(
        await bulkCspmCheckSuppressions(scheduleId, [], { reset: true })
      );
      setPending(new Map());
      setResetOpen(false);
      enqueueSnackbar(message, { variant: "success" });
      severityCacheRef.current = { key: "", items: null };
      sortListRef.current = { key: "", items: null };
      await loadChecks();
    } catch (resetError) {
      enqueueSnackbar(resetError.message || "Unable to reset check suppressions.", { variant: "error" });
    } finally {
      setSaving(null);
    }
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const scheduleDetail = schedule
    ? `Schedule: ${formatScheduleName(schedule)} | ${accountLabels.noun}: ${schedule.account_number || schedule.account_id || "N/A"}`
    : "Viewing CSPM checks for the selected schedule";

  return (
    <ConfigCard>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          separator={<Box component="span" sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#94a3b8", fontSize: "0.8125rem", mx: 0.5 }}>/</Box>}
          aria-label="breadcrumb"
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/configuration/cspm")}
            sx={{
              fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.8125rem",
              fontWeight: 400,
              cursor: "pointer",
              px: 0.5,
              "&:hover": { color: "#334155" },
            }}
          >
            CSPM Configuration
          </Link>
          <Typography variant="body2" sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#475569", fontSize: "0.8125rem", fontWeight: 500, px: 0.5 }}>
            Checks
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          borderBottom: "1px solid #e5e7eb",
          pb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontWeight: 700, color: "#111827", fontSize: "1rem", mb: 0.5 }}>
            Checks
          </Typography>
          <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.5 }}>
            {scheduleDetail}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={() => history.push("/configuration/cspm")}
          sx={BACK_BUTTON_SX}
        >
          Back
        </Button>
      </Box>

      <ErrorBanner message={error} onRetry={loadChecks} />

      {loading && !checksResult ? <LoadingState label="Loading checks..." /> : null}

      {checksResult ? (
        <TableFrame
          refreshing={refreshing || loading}
          footer={
            <ListFooter
              page={page}
              pageSize={pageSize}
              total={checksResult.total || 0}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              disabled={Boolean(saving)}
              onPageChange={setPage}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                storePageSize(PAGE_SIZE_KEY, nextSize);
                setPage(1);
              }}
            />
          }
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                flex: "1 1 520px",
                minWidth: 0,
              }}
            >
              <TextField
                size="small"
                placeholder="Search..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                inputProps={{ "aria-label": "Search checks" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...TOOLBAR_FIELD_SX, flex: "1 1 220px", width: { xs: "100%", sm: 240 }, maxWidth: { sm: 280 } }}
              />
              <FormControl size="small" sx={{ flex: { xs: "1 1 160px", sm: "0 0 200px" }, width: { xs: "auto", sm: 200 }, minWidth: 0 }}>
                <Select
                  displayEmpty
                  value={service}
                  inputProps={{ "aria-label": "Filter by service" }}
                  onChange={(event) => {
                    setService(event.target.value);
                    setPage(1);
                  }}
                  sx={TOOLBAR_SELECT_SX}
                  MenuProps={{
                    ...MENU_PROPS,
                    PaperProps: {
                      sx: {
                        ...MENU_PROPS.PaperProps.sx,
                        minWidth: 220,
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        color: selected ? "#111827" : "#94a3b8",
                        fontWeight: selected ? 500 : 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selected || "Service"}
                    </Box>
                  )}
                >
                  <MenuItem value="">All services</MenuItem>
                  {servicesLoading && serviceOptions.length === 0 ? (
                    <MenuItem value="__loading" disabled>Loading services...</MenuItem>
                  ) : null}
                  {serviceOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: { xs: "1 1 140px", sm: "0 0 160px" }, width: { xs: "auto", sm: 160 } }}>
                <Select
                  displayEmpty
                  value={severity}
                  inputProps={{ "aria-label": "Filter by severity" }}
                  onChange={(event) => {
                    const next = severityKey(event.target.value);
                    setSeverity(SEVERITY_OPTIONS.includes(next) ? next : "");
                    setPage(1);
                  }}
                  sx={TOOLBAR_SELECT_SX}
                  MenuProps={MENU_PROPS}
                  renderValue={(selected) => (
                    <Box sx={{ color: selected ? "#111827" : "#94a3b8", fontWeight: selected ? 500 : 400 }}>
                      {selected ? formatLabel(selected) : "Severity"}
                    </Box>
                  )}
                >
                  <MenuItem value="">All severities</MenuItem>
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{formatLabel(option)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                flexShrink: 0,
                ml: { lg: "auto" },
              }}
            >
              {changeCount > 0 ? (
                <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#c2410c", fontSize: "0.8125rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {changeCount} unsaved {changeCount === 1 ? "change" : "changes"}
                </Typography>
              ) : null}
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
                disabled={Boolean(saving)}
                onClick={() => setResetOpen(true)}
                sx={RESET_BUTTON_SX}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                disabled={changeCount === 0 || Boolean(saving)}
                onClick={saveChanges}
                sx={SAVE_BUTTON_SX}
              >
                {saving === "save" ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
              </Button>
            </Box>
          </Box>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ ...HEADER_CELL_SX, pl: 1.5, width: 56 }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={someChecked}
                      disabled={selectableItems.length === 0 || Boolean(saving)}
                      onChange={(event) => togglePage(event.target.checked)}
                      inputProps={{ "aria-label": "Suppress all checks on this page" }}
                      sx={CHECKBOX_SX}
                    />
                  </TableCell>
                  <EditAccountSortableHeader field="check" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Check
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader field="severity" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Severity
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader field="service" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Service
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader field="resourceType" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Resource Type
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader field="reason" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Reason
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader field="status" orderBy={orderBy} order={order} onSort={handleSort} align="left" headerCellSx={HEADER_CELL_SX}>
                    Status
                  </EditAccountSortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <EmptyRow colSpan={7} label="No checks found" />
                  </TableRow>
                ) : (
                  items.map((check) => {
                    const state = resolveCheckState(check, pending);
                    const suppressed = Boolean(state.suppressed);
                    const active = readFlag(check.is_active);
                    const title = check.title || check.check_id || "—";
                    return (
                      <TableRow
                        key={check.check_id || title}
                        sx={{
                          backgroundColor: suppressed ? "#fff7ed" : "#fff",
                          "&:hover": { backgroundColor: suppressed ? "#ffedd5" : "#f8fafc" },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ ...BODY_CELL_SX, pl: 1.5 }}>
                          <Checkbox
                            size="small"
                            checked={suppressed}
                            disabled={!check.check_id || Boolean(saving)}
                            onChange={(event) => commitCheck(check, {
                              suppressed: event.target.checked,
                              reason: event.target.checked ? state.reason || state.originalReason || "" : state.reason,
                            })}
                            inputProps={{ "aria-label": `Suppress ${title}` }}
                            sx={CHECKBOX_SX}
                          />
                        </TableCell>
                        <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 320 }}>
                          <TruncatedTooltipText title={check.title || check.check_id} sx={{ ...CELL_TEXT, fontWeight: 600, color: "#0f172a" }}>
                            {title}
                          </TruncatedTooltipText>
                          {check.check_id && check.check_id !== check.title ? (
                            <TruncatedTooltipText sx={{ ...CELL_TEXT, color: "#94a3b8", fontSize: "0.75rem", fontWeight: 500 }}>
                              {check.check_id}
                            </TruncatedTooltipText>
                          ) : null}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {check.severity ? (
                            <Chip label={formatLabel(check.severity)} size="small" sx={{ ...CHIP_SX, ...severityStyle(check.severity) }} />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 160 }}>
                          <TruncatedTooltipText sx={CELL_TEXT}>{check.service || "—"}</TruncatedTooltipText>
                        </TableCell>
                        <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 180 }}>
                          <TruncatedTooltipText sx={CELL_TEXT}>{check.resource_type || "—"}</TruncatedTooltipText>
                        </TableCell>
                        <TableCell sx={{ ...BODY_CELL_SX, minWidth: 180 }}>
                          <TextField
                            size="small"
                            disabled={!suppressed || !check.check_id || Boolean(saving)}
                            value={suppressed ? state.reason : ""}
                            placeholder={suppressed ? DEFAULT_SUPPRESS_REASON : ""}
                            onChange={(event) => commitCheck(check, { reason: event.target.value })}
                            inputProps={{ "aria-label": `Reason for ${title}` }}
                            sx={REASON_FIELD_SX}
                          />
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {active === null ? (
                            "—"
                          ) : (
                            <Chip
                              label={active ? "Active" : "Inactive"}
                              size="small"
                              sx={{
                                ...CHIP_SX,
                                backgroundColor: active ? "#ecfdf5" : "#f1f5f9",
                                color: active ? "#15803d" : "#64748b",
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableFrame>
      ) : null}

      <Dialog
        open={resetOpen}
        onClose={() => {
          if (saving !== "reset") setResetOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontWeight: 700, fontSize: "1.15rem", color: "#fff", letterSpacing: "-0.02em" }}>
            Reset check suppressions
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              if (saving !== "reset") setResetOpen(false);
            }}
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: saving === "reset" ? "default" : "pointer",
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <DialogContentText sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#334155", fontSize: "0.9375rem", lineHeight: 1.5, m: 0 }}>
            Remove every CSPM check suppression for this schedule, including checks that are not on the current page.
            {changeCount > 0 ? " Unsaved changes on this page will be discarded." : ""}
          </DialogContentText>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setResetOpen(false)}
            disabled={saving === "reset"}
            sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", textTransform: "none", fontWeight: 500, "&:hover": { backgroundColor: "#f1f5f9" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={resetSuppressions}
            disabled={saving === "reset"}
            sx={{
              fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
              backgroundColor: "#ef4444",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#dc2626", boxShadow: "none" },
            }}
          >
            {saving === "reset" ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Reset"}
          </Button>
        </Box>
      </Dialog>
    </ConfigCard>
  );
};

export default CheckSuppressions;
